import LogoutIcon from '@mui/icons-material/Logout';
import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
  Theme,
  Toolbar,
  Typography,
} from '@mui/material';
import React, { memo, useCallback, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

import deletedCompaniesIcon from '~/assets/icons/deleted-companies.svg';
import externalUsersIcon from '~/assets/icons/external-users.svg';
import usersIcon from '~/assets/icons/users.svg';
import avatar from '~/assets/images/avatar.svg';
import logo from '~/assets/images/logo.svg';
import { LogoutModal } from '~/components/LogoutModal';
import { ADMIN_ROUTES } from '~/constants/routes';
import { useAppSelector } from '~/store/hooks';
import { selectUser } from '~/store/slices/user/selectors';

import styles from './styles.module.scss';

interface NavLink {
  label: string;
  icon: string;
  route: ADMIN_ROUTES;
}

const ICON_SX: SxProps<Theme> = { color: 'var(--text-3-color)', width: '28px', height: '28px' };
const NAV_LINKS: NavLink[] = [
  { label: 'Users', icon: usersIcon, route: ADMIN_ROUTES.USERS },
  {
    label: 'External Users',
    icon: externalUsersIcon,
    route: ADMIN_ROUTES.EXTERNAL_USERS,
  },
  { label: 'Deleted Companies', icon: deletedCompaniesIcon, route: ADMIN_ROUTES.DELETED_COMPANIES },
];

const Component: React.FC = () => {
  const user = useAppSelector(selectUser);
  const location = useLocation();
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);

  const showLogoutModal = useCallback(() => {
    setLogoutModalVisible(true);
  }, []);

  const hideLogoutModal = useCallback(() => {
    setLogoutModalVisible(false);
  }, []);

  return (
    <>
      <div className={styles.page}>
        <Drawer
          anchor="left"
          sx={{
            width: 'var(--sidebar-width)',
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 'var(--sidebar-width)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              backgroundColor: 'var(--gray-3-color)',
            },
          }}
          variant="permanent"
        >
          <div>
            <Toolbar>
              <Typography align="center" color="var(--text-3-color)" flex="1" variant="h5">
                ADMIN PANEL
              </Typography>
            </Toolbar>
            <Divider />
            <List>
              {NAV_LINKS.map(navLink => (
                <ListItem key={navLink.label}>
                  <Link className={styles.page__link} to={navLink.route}>
                    <ListItemButton
                      selected={location.pathname === navLink.route}
                      sx={{ borderRadius: '4px' }}
                    >
                      <ListItemIcon sx={{ minWidth: '40px' }}>
                        <img alt={navLink.label} src={navLink.icon} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            color="var(--main-1-color)"
                            fontWeight="500"
                            variant="subtitle2"
                          >
                            {navLink.label}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>
              ))}
            </List>
          </div>
          <div>
            <Divider />
            <Toolbar>
              <List sx={{ flex: 1 }}>
                <ListItem>
                  <ListItemButton onClick={showLogoutModal}>
                    <ListItemIcon>
                      <img alt="avatar" src={avatar} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          color="var(--text-3-color)"
                          fontWeight="500"
                          variant="subtitle1"
                        >
                          {user?.first_name} {user?.last_name}
                        </Typography>
                      }
                    />
                    <LogoutIcon sx={ICON_SX} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Toolbar>
          </div>
        </Drawer>
        <div className={styles.page__container}>
          <header className={styles.page__header}>
            <Link className={styles['page__home-link']} to={ADMIN_ROUTES.HOME}>
              <img alt="logo" src={logo} />
            </Link>
          </header>
          <Outlet />
        </div>
      </div>
      <LogoutModal open={logoutModalVisible} onClose={hideLogoutModal} />
    </>
  );
};

export const PageWithSidebar = memo(Component);
